
import axios from 'axios';

import {
  defineComponent,
  reactive,
  ref,
  onMounted,
  computed,
} from 'vue';

import { useRouter } from 'vue-router';

import PrimeVueToast from 'primevue/toast';

import { useToast } from 'primevue/usetoast';

import PrimeVueConfirmPopup from 'primevue/confirmpopup';

import type {
  EntriesPaginator,
  Projects,
  Creatives,
  Creative,
} from '@/types';

import {
  ENTRIES,
  CREATIVE_TYPES,
  CREATIVE_TYPES_MAP,
  CREATIVE_IMAGE_FORMATS_MAP,
  CREATIVE_IMAGE_FORMATS,
  CREATIVE_APPROVAL_STATUSES,
  CREATIVE_APPROVAL_STATUSES_MAP,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntriesNext from '@/composable/useEntries@next';

import useUrlState from '@/composable/useUrlState';

import useCreativeActions from '@/composable/useCreativeActions';

import UserColumn from '@/components/UserColumn.vue';

import CreativeApprovalStatus from '@/components/CreativeApprovalStatus.vue';

export default defineComponent({
  components: {
    PrimeVueToast,
    PrimeVueConfirmPopup,
    UserColumn,
    CreativeApprovalStatus,
  },
  setup() {
    const router = useRouter();

    const toast = useToast();

    const { activeUser: user } = useUser();

    const {
      cloningCreativeId,
      confirmCloneCreative,
      deletingCreativeId,
      confirmDeleteCreative,
      restoringCreativeId,
      restoreCreative,
    } = useCreativeActions();

    const {
      entries: projects,
      entriesLoading: projectsLoading,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects', ENTRIES);

    const creatives = reactive<Creatives>({ ...ENTRIES });

    const creativesFilters = useUrlState('filters', {
      id: null,
      project_id: null,
      user_id: null,
      type: null,
      image_format: null,
      name: null,
      approval_status: null,
      trashed: null,
    });

    const usersParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? creativesFilters.project_id : undefined,
    }));

    const previousPaginator = ref<EntriesPaginator>({} as EntriesPaginator);

    const fetchCreatives = async (creativesPaginator: EntriesPaginator): Promise<void> => {
      previousPaginator.value = creativesPaginator;

      const response = await axios.post(
        '/api/creative/getCreativesPaginator',
        {
          ...creativesPaginator,
          ...creativesFilters,
        },
      );

      Object.assign(creatives, response.data);
    };

    const refreshCreatives = (): Promise<void> => fetchCreatives(previousPaginator.value);

    const withTrashed = (value: boolean): void => {
      creativesFilters.trashed = value ? 'with' : null;

      refreshCreatives();
    };

    const onProjectChange = (): void => {
      creativesFilters.user_id = null;
    };

    const onRestoreSuccess = (id: number): void => {
      const creative = creatives.data.find((item) => item.id === id);

      if (creative) {
        creative.deleted_at = null;
      }
    };

    const onDeleteSuccess = (id: number): void => {
      const creative = creatives.data.find((item) => item.id === id);

      if (creative) {
        creative.deleted_at = new Date().toISOString();
      }
    };

    const onCloneSuccess = async (newId: number): Promise<void> => {
      await router.push(`/creatives/${newId}`);
    };

    const previewCreative = (creative: Creative): void => {
      toast.removeAllGroups();

      toast.add({
        severity: 'info',
        styleClass: 'p-toast-push',
        detail: creative,
        group: 'bc',
        life: 5000,
      });
    };

    onMounted(async (): Promise<void> => {
      if (user.isSuperAdmin()) {
        await fetchProjects();
      }
    });

    return {
      CREATIVE_TYPES,
      CREATIVE_TYPES_MAP,
      CREATIVE_IMAGE_FORMATS,
      CREATIVE_IMAGE_FORMATS_MAP,
      CREATIVE_APPROVAL_STATUSES,
      CREATIVE_APPROVAL_STATUSES_MAP,
      user,
      projectsLoading,
      projects,
      creatives,
      creativesFilters,
      usersParams,
      fetchCreatives,
      withTrashed,

      onProjectChange,
      cloningCreativeId,
      confirmCloneCreative,
      deletingCreativeId,
      confirmDeleteCreative,
      restoringCreativeId,
      restoreCreative,
      onRestoreSuccess,
      onDeleteSuccess,
      onCloneSuccess,
      previewCreative,
    };
  },
});
